import { buildBceAuthHeaders, uriEncode } from '../../shared/bce-signer';

// 百度智能云 VOD（智能点播平台）API 客户端。
// 鉴权使用 AK/SK（bce-auth-v1），与千帆通道的 BAIDU_API_KEY 无关。
// 文档: https://cloud.baidu.com/doc/VOD/s/fmhiy9vqk （字幕擦除）
//       https://cloud.baidu.com/doc/VOD/s/Am4j8w1t0 （拉取上传）

const DEFAULT_BASE_URL = 'https://vod.bj.baidubce.com';

export function getBaiduVodBaseUrl(): string {
    return (process.env.BAIDU_VOD_BASE_URL || DEFAULT_BASE_URL).replace(/\/$/, '');
}

export function getBaiduVodAk(): string {
    const ak = process.env.BAIDU_VOD_AK;
    if (!ak) throw new Error('Missing BAIDU_VOD_AK');
    return ak;
}

function getBaiduVodSk(): string {
    const sk = process.env.BAIDU_VOD_SK;
    if (!sk) throw new Error('Missing BAIDU_VOD_SK');
    return sk;
}

export function buildBaiduVodHeaders(params: {
    method: 'GET' | 'POST' | 'PUT';
    path: string;
    query?: Record<string, string | number | undefined>;
}): Record<string, string> {
    const host = new URL(getBaiduVodBaseUrl()).host;
    return {
        'Content-Type': 'application/json',
        ...buildBceAuthHeaders({
            ak: getBaiduVodAk(),
            sk: getBaiduVodSk(),
            method: params.method,
            path: params.path,
            query: params.query,
            host,
        }),
    };
}

/** 用与签名一致的 RFC 3986 编码构建查询串，避免特殊字符导致签名不匹配。 */
export function buildVodQueryString(
    query?: Record<string, string | number | undefined>,
): string {
    if (!query) return '';
    return Object.entries(query)
        .filter(([, value]) => value !== undefined)
        .map(([key, value]) => `${uriEncode(key)}=${uriEncode(String(value))}`)
        .join('&');
}

export async function vodRequest<T>(params: {
    method: 'GET' | 'POST' | 'PUT';
    path: string;
    query?: Record<string, string | number | undefined>;
    body?: Record<string, unknown>;
}): Promise<T> {
    const { method, path, query, body } = params;
    const queryString = buildVodQueryString(query);
    const url = `${getBaiduVodBaseUrl()}${path}${queryString ? `?${queryString}` : ''}`;

    const response = await fetch(url, {
        method,
        headers: buildBaiduVodHeaders({ method, path, query }),
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal: AbortSignal.timeout(60_000),
    });

    const text = await response.text();
    let data: any = {};
    try {
        if (text) data = JSON.parse(text);
    } catch {
        data = { raw: text };
    }

    if (!response.ok) {
        const detail = data?.message?.details || data?.message || data?.code || text || '';
        throw new Error(
            `Baidu VOD ${method} ${path} failed: ${response.status} ${response.statusText}${detail ? ` — ${typeof detail === 'string' ? detail : JSON.stringify(detail)}` : ''}`,
        );
    }
    return data as T;
}

// ==================== 拉取上传 ====================

/** 拉取上传：将公网视频 URL 拉入 VOD，返回拉取任务 ID（异步）。 */
export async function fetchMediaFromUrl(params: {
    url: string;
    name?: string;
    /** 媒资过期自动删除（秒），[3600, 180天]，用于控制 VOD 存储成本 */
    deleteAfterSeconds?: number;
}): Promise<{ taskId: string }> {
    const data = await vodRequest<{ taskId?: string }>({
        method: 'POST',
        path: '/v2/medias/fetch',
        body: {
            url: params.url,
            ...(params.name ? { name: params.name } : {}),
            ...(params.deleteAfterSeconds ? { deleteAfterSeconds: params.deleteAfterSeconds } : {}),
        },
    });
    if (!data.taskId) throw new Error('Baidu VOD fetch did not return taskId');
    return { taskId: data.taskId };
}

export type VodTaskDetail = {
    taskId?: string;
    type?: string;
    status?: string;
    mediaId?: string;
    mediaFetchTaskInfo?: {
        mediaId?: string;
        mediaBasicInfo?: { mediaId?: string; [key: string]: unknown };
        [key: string]: unknown;
    };
    [key: string]: unknown;
};

/** 查询任务详情（含拉取上传任务）：成功后可拿到生成的 mediaId。 */
export async function getVodTaskDetail(taskId: string): Promise<VodTaskDetail> {
    return vodRequest<VodTaskDetail>({
        method: 'GET',
        path: `/v2/tasks/${taskId}`,
    });
}

export function extractFetchTaskMediaId(task: VodTaskDetail): string | undefined {
    // 实测拉取任务成功后 mediaId 位于 mediaFetchTaskInfo.mediaBasicInfo.mediaId
    const info = task.mediaFetchTaskInfo;
    const mediaId = info?.mediaBasicInfo?.mediaId || info?.mediaId || task.mediaId;
    return typeof mediaId === 'string' && mediaId ? mediaId : undefined;
}

export type VodMediaDetail = {
    mediaId?: string;
    /** PENDING / RUNNING / FAILED / PUBLISHED / DISABLED / BANNED */
    status?: string;
    meta?: { durationInSeconds?: number; [key: string]: unknown };
    [key: string]: unknown;
};

/** 查询媒资详情：status 变为 PUBLISHED 后媒资可用。 */
export async function getVodMediaDetail(mediaId: string): Promise<VodMediaDetail> {
    return vodRequest<VodMediaDetail>({
        method: 'GET',
        path: `/v2/medias/${mediaId}`,
    });
}

export type VodDesubtitleType = 'dialog' | 'global' | 'manual';
